import { useState } from 'react'
import { UserPlus } from '@phosphor-icons/react'
import { useCan } from '@/auth/context'
import { ChoiceGroup, Field, Input } from '@/ui'
import { errorMessage } from '../api/core'
import type { UserOption } from '../api/reference'
import {
  useAddVillageTeamMember,
  useClearPin,
  useDeviceTeam,
  useRemoveVillageTeamMember,
  useSetPin,
  type DeviceRow,
  type DeviceTeamMember,
} from '../api/setup'
import {
  emptyVillageTeamForm,
  pinProblem,
  validateVillageTeamForm,
  villageTeamBody,
  type VillageTeamForm,
  type VillageTeamRole,
} from '../model/teamForm'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { FormDialog } from './FormDialog'
import { PersonPicker } from './PersonPicker'
import { Section } from './bits'
import { TeamTable } from './TeamTable'

/**
 * The people a phone downloads for PIN sign-in (the same list as its roster), with their PIN
 * status. On a village phone the office adds and removes people here; a site team is edited
 * under Locations. Changes reach the phone when it taps "Update farmer list".
 */
export function PhoneTeamSection({ device }: { device: DeviceRow }) {
  const can = useCan()
  const allowed = can('rosters:read')
  const q = useDeviceTeam(device.id, allowed)
  const [adding, setAdding] = useState(false)
  const [pinFor, setPinFor] = useState<DeviceTeamMember | null>(null)
  const [removing, setRemoving] = useState<DeviceTeamMember | null>(null)
  const remove = useRemoveVillageTeamMember()
  const groupId = device.groupId
  if (!allowed) return null
  const team = q.data ?? []
  const listName = groupId ? 'farmer list' : 'store list'

  return (
    <Section
      title="Team on this phone"
      eyebrow="Who can sign in"
      actions={
        groupId && (
          <ActionButton
            perm="teams:manage"
            hide
            size="sm"
            variant="secondary"
            leadingIcon={<UserPlus />}
            onClick={() => setAdding(true)}
          >
            Add person
          </ActionButton>
        )
      }
    >
      {q.isError ? (
        <p className="text-[14px] text-muted">{errorMessage(q.error)}</p>
      ) : q.isLoading ? (
        <p className="text-[14px] text-muted">Loading…</p>
      ) : !team.length ? (
        <p className="text-[14px] text-muted">
          {groupId
            ? 'No one is on this team yet. Add the operator and the supervisor, then set their PINs.'
            : `No one is on this team yet. Add people under Locations → ${device.locationName ?? 'the site'} → Site team.`}
        </p>
      ) : (
        <TeamTable
          rows={team}
          onPin={setPinFor}
          onRemove={groupId ? setRemoving : undefined}
          removePerm="teams:manage"
        />
      )}
      <p className="mt-3 text-[12.5px] text-muted">
        Changes reach the phone when it taps “Update {listName}”.
      </p>

      {groupId && adding && (
        <AddVillagePersonDialog
          groupId={groupId}
          villageName={device.groupName ?? 'this village'}
          onTeam={new Set(team.map((m) => m.userId))}
          onClose={() => setAdding(false)}
        />
      )}
      {pinFor && (
        <PinDialog userId={pinFor.userId} name={pinFor.name} hasPin={pinFor.hasPin} onClose={() => setPinFor(null)} />
      )}
      {groupId && (
        <ConfirmDialog
          open={Boolean(removing)}
          onClose={() => setRemoving(null)}
          title={`Remove ${removing?.name ?? ''} from ${device.groupName ?? 'the village'}?`}
          description="They can no longer sign in on this village's phones after the next list update. Their account and roles stay."
          confirmLabel="Remove"
          tone="danger"
          loading={remove.isPending}
          onConfirm={async () => {
            if (!removing) return
            await remove.mutateAsync({ groupId, userId: removing.userId })
            setRemoving(null)
          }}
        />
      )}
    </Section>
  )
}

function AddVillagePersonDialog({
  groupId,
  villageName,
  onTeam,
  onClose,
}: {
  groupId: string
  villageName: string
  onTeam: ReadonlySet<string>
  onClose: () => void
}) {
  const [form, setForm] = useState<VillageTeamForm>(emptyVillageTeamForm)
  const [person, setPerson] = useState<UserOption | null>(null)
  const add = useAddVillageTeamMember()
  const set = (patch: Partial<VillageTeamForm>) => setForm((f) => ({ ...f, ...patch }))
  const problem = validateVillageTeamForm(form, onTeam)
  const showProblem = problem && (form.mode === 'existing' ? Boolean(form.userId) : form.firstName !== '')

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Add to ${villageName}`}
      description="They can sign in on this village's phones with their PIN."
      saveLabel="Add"
      loading={add.isPending}
      canSave={!problem}
      onSave={() => add.mutateAsync({ groupId, body: villageTeamBody(form) })}
    >
      <Field label="Role" required>
        <ChoiceGroup<VillageTeamRole>
          value={form.role}
          onChange={(role) => set({ role })}
          options={[
            { value: 'village_operator', label: 'Operator' },
            { value: 'supervisor', label: 'Supervisor' },
          ]}
        />
      </Field>
      <Field label="Person" required>
        <ChoiceGroup<VillageTeamForm['mode']>
          value={form.mode}
          onChange={(mode) => set({ mode })}
          options={[
            { value: 'existing', label: 'Already has an account' },
            { value: 'new', label: 'New person' },
          ]}
        />
      </Field>
      {form.mode === 'existing' ? (
        <Field label="Search" required error={showProblem ? problem : null}>
          <PersonPicker
            value={person}
            onChange={(u) => {
              setPerson(u)
              set({ userId: u?.id ?? null })
            }}
            placeholder="Search by name"
            filter={(u) => !u.farmerCode && !u.roles.includes('farmer')}
            label="Person"
          />
        </Field>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required error={showProblem && !form.firstName.trim() ? problem : null}>
              <Input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} data-autofocus />
            </Field>
            <Field label="Last name">
              <Input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
            </Field>
          </div>
          <Field
            label="Phone number"
            hint="Optional"
            error={showProblem && form.firstName.trim() ? problem : null}
          >
            <Input
              value={form.contactNo}
              onChange={(e) => set({ contactNo: e.target.value })}
              inputMode="tel"
              autoComplete="off"
            />
          </Field>
          <p className="text-[12.5px] text-muted">
            A phone-only account: they never sign in online, only with a PIN on the phone. Set the PIN
            after adding them.
          </p>
        </>
      )}
    </FormDialog>
  )
}

/** Set, change or clear a phone-only worker's PIN. The PIN is never shown again after saving. */
export function PinDialog({
  userId,
  name,
  hasPin,
  onClose,
}: {
  userId: string
  name: string
  hasPin: boolean
  onClose: () => void
}) {
  const [pin, setPin] = useState('')
  const [again, setAgain] = useState('')
  const [confirmClear, setConfirmClear] = useState(false)
  const setPinM = useSetPin()
  const clear = useClearPin()
  const problem = pinProblem(pin, again)
  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 6)

  if (confirmClear)
    return (
      <ConfirmDialog
        open
        onClose={onClose}
        title={`Clear ${name}'s PIN?`}
        description="They cannot sign in on the phone until a new PIN is set and the phone updates its list."
        confirmLabel="Clear PIN"
        tone="danger"
        loading={clear.isPending}
        onConfirm={() => clear.mutateAsync({ userId }).then(onClose)}
      />
    )

  return (
    <FormDialog
      open
      onClose={onClose}
      title={hasPin ? `Change ${name}'s PIN` : `Set ${name}'s PIN`}
      description="Tell them the PIN in person. It is not shown again."
      saveLabel="Save PIN"
      loading={setPinM.isPending}
      canSave={!problem}
      onSave={() => setPinM.mutateAsync({ userId, pin })}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="PIN" required hint="4 to 6 digits" error={pin.length >= 4 && problem && problem !== 'The two PINs are not the same' ? problem : null}>
          <Input
            value={pin}
            onChange={(e) => setPin(digits(e.target.value))}
            inputMode="numeric"
            autoComplete="off"
            className="font-mono tracking-[0.3em]"
            data-autofocus
          />
        </Field>
        <Field label="Type it again" required error={again.length >= pin.length && pin.length >= 4 && problem === 'The two PINs are not the same' ? problem : null}>
          <Input
            value={again}
            onChange={(e) => setAgain(digits(e.target.value))}
            inputMode="numeric"
            autoComplete="off"
            className="font-mono tracking-[0.3em]"
          />
        </Field>
      </div>
      {hasPin && (
        <button
          type="button"
          className="self-start text-[13px] text-danger underline underline-offset-2"
          onClick={() => setConfirmClear(true)}
        >
          Clear the PIN instead
        </button>
      )}
    </FormDialog>
  )
}
